import Tooltip from '@mui/material/Tooltip';
import React, { createRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { audioApi } from 'api';
import { useModal } from 'shared/hooks';
import { TaskKeysType } from 'shared/types';
import { ItemActionModal } from 'shared/ui/ItemActionModal';

import micSvg from '../../../assets/img/mic.svg';
import micRedSvg from '../../../assets/img/mic_red.svg';

interface Props {
  currentICLId: string;
  id: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  audioUrl?: string;
  remoteTaskId?: string;
  audioFile: { file: File; url: string } | null;
  setAudioFile: (file: { file: File; url: string } | null) => void;
  hasText?: boolean;
  closeModal?: () => void;
}

export const AudioInput = ({
  currentICLId,
  id,
  handleChangeTaskByKey,
  audioUrl,
  remoteTaskId,
  audioFile,
  setAudioFile,
  hasText,
  closeModal,
}: Props) => {
  const { t } = useTranslation();
  const audioInputRef = createRef<HTMLInputElement>();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen: isRemovingAudioModalOpen, close: closeRemovingAudioModal, open: openRemovingAudioModal } = useModal();

  const handleAddAudioLocal = async () => {
    const audioInput = audioInputRef.current;

    if (!audioInput || !audioInput.files) {
      return;
    }

    const file = audioInput.files[0];
    setAudioFile({ file, url: URL.createObjectURL(file) });
  };

  const sendAudio = async () => {
    const audioInput = audioInputRef.current;

    if (!audioInput || !audioInput.files) {
      return;
    }

    setIsLoading(true);

    const file = audioInput.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const gottenAudioUrl = await audioApi.addAudio(file, currentICLId, id);
      handleChangeTaskByKey(id, 'audioUrl', gottenAudioUrl);

      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsLoading(false);
  };

  const removeAudio = async () => {
    setIsLoading(true);

    try {
      toast.success(t('Successfully removed!'));
      remoteTaskId && (await audioApi.removeAudio(audioUrl ?? (audioFile?.url as string), currentICLId, id));
      setAudioFile(null);
      closeRemovingAudioModal();
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsLoading(false);
  };

  return (
    <div className="ql-image button-icon">
      <Tooltip
        title={audioUrl || audioFile ? t('remove audio') : t('add audio')}
        componentsProps={{
          popper: {
            sx: {
              ['& .MuiTooltip-arrow']: {
                color: '#9CDFFC',
              },
              ['& .MuiTooltip-tooltip']: {
                backgroundColor: '#9CDFFC',
                color: '#000000',
                fontSize: '14px',
                padding: '14px',
                borderRadius: '8px',
              },
            },
          },
        }}
        arrow
      >
        <button
          onClick={() => {
            closeModal && closeModal();

            if (audioUrl || audioFile) {
              openRemovingAudioModal();
            } else {
              audioInputRef.current?.click();
            }
          }}
          className={
            hasText
              ? 'flex items-center gap-[10px]'
              : 'btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600'
          }
        >
          {audioUrl || audioFile ? <img src={micRedSvg} alt="mic" /> : <img src={micSvg} alt="mic" />}

          <input
            id="audioInput"
            type="file"
            className="hidden"
            accept="audio/aac,audio/mp3,audio/vbr,audio/audible,audio/aiff,audio/wav,audio/alac,audio/*"
            ref={audioInputRef}
            onChange={() => (remoteTaskId ? sendAudio() : handleAddAudioLocal())}
            disabled={isLoading || !!audioUrl || !!audioFile}
            title={t('add audio')}
          />

          {hasText && (
            <p className="!mb-0 text-base font-bold text-[#242C2C]">
              {audioUrl || audioFile ? t('remove audio') : t('add audio')}
            </p>
          )}
        </button>
      </Tooltip>

      <ItemActionModal
        isOpen={isRemovingAudioModalOpen}
        close={closeRemovingAudioModal}
        title={t('Remove Audio')}
        onHandle={removeAudio}
        isSubmitButtonDisabled={isLoading}
        buttonTitle="delete"
      />
    </div>
  );
};
